import type { IPetRepository } from '@yawi/domain';
import type { CreatePetDTO, CreateVaccinationDTO, Pet, UpdatePetDTO, UpdateVaccinationDTO, Vaccination, VaccineType } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// API Response types
interface ApiPetResponse {
  id: string;
  ownerId?: string;
  ownerMemberId?: string;
  unitId?: string | null;
  ownerName?: string | null;
  name: string;
  type: 'DOG' | 'CAT' | 'BIRD' | 'OTHER';
  breed?: string | null;
  color?: string | null;
  birthDate?: string | null;
  // Backend may return a human-readable string (e.g. "2 años 3 meses"); mock mode returns a number.
  age?: number | string | null;
  chipId?: string | null;
  photoUrl?: string | null;
  photoFileId?: string | null;
  notes?: string | null;
  vaccinations?: ApiVaccinationResponse[];
  createdAt: string;
  updatedAt: string;
}

interface ApiVaccinationResponse {
  id: string;
  petId: string;
  vaccineType: VaccineType;
  vaccineName?: string | null;
  appliedAt: string;
  nextDoseAt?: string | null;
  veterinarian?: string | null;
  clinic?: string | null;
  batchNumber?: string | null;
  notes?: string | null;
  isOverdue?: boolean;
  daysUntilNextDose?: number;
  createdAt: string;
  updatedAt: string;
}

interface ApiCreatePetRequest {
  name: string;
  type: 'DOG' | 'CAT' | 'BIRD' | 'OTHER';
  breed?: string;
  color?: string;
  birthDate?: string;
  chipId?: string;
  photoUrl?: string;
  photoFileId?: string;
  notes?: string;
  unitId?: string;
}

interface ApiUpdatePetRequest {
  name?: string;
  type?: 'DOG' | 'CAT' | 'BIRD' | 'OTHER';
  breed?: string;
  color?: string;
  birthDate?: string;
  chipId?: string;
  photoUrl?: string;
  photoFileId?: string;
  notes?: string;
}

interface ApiCreateVaccinationRequest {
  vaccineType: VaccineType;
  vaccineName?: string;
  appliedAt: string;
  nextDoseAt?: string;
  veterinarian?: string;
  clinic?: string;
  notes?: string;
}

interface ApiUpdateVaccinationRequest {
  vaccineType?: VaccineType;
  vaccineName?: string;
  appliedAt?: string;
  nextDoseAt?: string;
  veterinarian?: string;
  clinic?: string;
  notes?: string;
}

// Mappers

/**
 * Resolve a numeric age (in years) for the Pet model.
 * The backend may return `age` as a human-readable string (e.g. "2 años 3 meses"),
 * but every client expects a number of years, so we derive it from birthDate and
 * only trust `age` when it is already numeric (e.g. mock mode).
 */
const resolvePetAge = (apiPet: ApiPetResponse): number | undefined => {
  if (typeof apiPet.age === 'number') return apiPet.age;
  if (!apiPet.birthDate) return undefined;
  const birth = new Date(apiPet.birthDate);
  if (Number.isNaN(birth.getTime())) return undefined;
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    years--;
  }
  return years >= 0 ? years : undefined;
};

const mapApiPetToPet = (apiPet: ApiPetResponse): Pet => ({
  id: apiPet.id,
  ownerMemberId: apiPet.ownerMemberId || apiPet.ownerId,
  unitId: apiPet.unitId ?? null,
  ownerName: apiPet.ownerName ?? null,
  name: apiPet.name,
  type: apiPet.type,
  breed: apiPet.breed || undefined,
  color: apiPet.color || undefined,
  birthDate: apiPet.birthDate || undefined,
  age: resolvePetAge(apiPet),
  chipId: apiPet.chipId || undefined,
  photoUrl: apiPet.photoUrl || undefined,
  photoFileId: apiPet.photoFileId || undefined,
  notes: apiPet.notes || undefined,
  vaccinations: apiPet.vaccinations?.map(mapApiVaccinationToVaccination) || [],
  createdAt: apiPet.createdAt,
  updatedAt: apiPet.updatedAt,
});

const mapApiVaccinationToVaccination = (apiVacc: ApiVaccinationResponse): Vaccination => ({
  id: apiVacc.id,
  petId: apiVacc.petId,
  vaccineType: apiVacc.vaccineType,
  vaccineName: apiVacc.vaccineName || undefined,
  appliedAt: apiVacc.appliedAt,
  nextDoseAt: apiVacc.nextDoseAt || undefined,
  veterinarian: apiVacc.veterinarian || undefined,
  clinic: apiVacc.clinic || undefined,
  batchNumber: apiVacc.batchNumber || undefined,
  notes: apiVacc.notes || undefined,
  isOverdue: apiVacc.isOverdue,
  daysUntilNextDose: apiVacc.daysUntilNextDose,
  createdAt: apiVacc.createdAt,
  updatedAt: apiVacc.updatedAt,
});

export class ApiPetRepository implements IPetRepository {
  // === Pet CRUD ===

  async getAllPets(): Promise<Pet[]> {
    const client = getApiClient();
    // This endpoint is paginated: the API client returns the envelope { data, meta }
    // for paginated responses (it only unwraps non-paginated { success, data }).
    const response = await client.get<ApiPetResponse[] | { data: ApiPetResponse[] }>('v1/pets');
    const list = Array.isArray(response) ? response : (response?.data ?? []);
    return list.map(mapApiPetToPet);
  }

  async getMyPets(): Promise<Pet[]> {
    const client = getApiClient();
    const response = await client.get<ApiPetResponse[]>('v1/pets/my-pets');
    return response.map(mapApiPetToPet);
  }

  async getPetById(id: string): Promise<Pet | null> {
    const client = getApiClient();
    try {
      const pet = await client.get<ApiPetResponse>(`v1/pets/${id}`);
      return mapApiPetToPet(pet);
    } catch {
      return null;
    }
  }

  async createPet(data: CreatePetDTO): Promise<Pet> {
    const client = getApiClient();
    const request: ApiCreatePetRequest = {
      name: data.name,
      type: data.type,
      breed: data.breed,
      color: data.color,
      birthDate: data.birthDate,
      chipId: data.chipId,
      photoUrl: data.photoUrl,
      photoFileId: data.photoFileId,
      notes: data.notes,
      unitId: data.unitId,
    };
    const apiPet = await client.post<ApiPetResponse>('v1/pets', request);
    return mapApiPetToPet(apiPet);
  }

  async updatePet(id: string, data: UpdatePetDTO): Promise<Pet> {
    const client = getApiClient();
    const request: ApiUpdatePetRequest = {
      name: data.name,
      type: data.type,
      breed: data.breed,
      color: data.color,
      birthDate: data.birthDate,
      chipId: data.chipId,
      photoUrl: data.photoUrl,
      photoFileId: data.photoFileId,
      notes: data.notes,
    };
    const apiPet = await client.put<ApiPetResponse>(`v1/pets/${id}`, request);
    return mapApiPetToPet(apiPet);
  }

  async deletePet(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/pets/${id}`);
  }

  // === Vaccinations ===

  async getVaccinations(petId: string): Promise<Vaccination[]> {
    const client = getApiClient();
    const response = await client.get<ApiVaccinationResponse[]>(`v1/pets/${petId}/vaccinations`);
    return response.map(mapApiVaccinationToVaccination);
  }

  async getVaccinationById(petId: string, vaccinationId: string): Promise<Vaccination | null> {
    const client = getApiClient();
    try {
      const vaccination = await client.get<ApiVaccinationResponse>(`v1/pets/${petId}/vaccinations/${vaccinationId}`);
      return mapApiVaccinationToVaccination(vaccination);
    } catch {
      return null;
    }
  }

  async createVaccination(petId: string, data: CreateVaccinationDTO): Promise<Vaccination> {
    const client = getApiClient();
    const request: ApiCreateVaccinationRequest = {
      vaccineType: data.vaccineType,
      vaccineName: data.vaccineName,
      appliedAt: data.appliedAt,
      nextDoseAt: data.nextDoseAt,
      veterinarian: data.veterinarian,
      clinic: data.clinic,
      notes: data.notes,
    };
    const apiVacc = await client.post<ApiVaccinationResponse>(`v1/pets/${petId}/vaccinations`, request);
    return mapApiVaccinationToVaccination(apiVacc);
  }

  async updateVaccination(petId: string, vaccinationId: string, data: UpdateVaccinationDTO): Promise<Vaccination> {
    const client = getApiClient();
    const request: ApiUpdateVaccinationRequest = {
      vaccineType: data.vaccineType,
      vaccineName: data.vaccineName,
      appliedAt: data.appliedAt,
      nextDoseAt: data.nextDoseAt,
      veterinarian: data.veterinarian,
      clinic: data.clinic,
      notes: data.notes,
    };
    const apiVacc = await client.put<ApiVaccinationResponse>(`v1/pets/${petId}/vaccinations/${vaccinationId}`, request);
    return mapApiVaccinationToVaccination(apiVacc);
  }

  async deleteVaccination(petId: string, vaccinationId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/pets/${petId}/vaccinations/${vaccinationId}`);
  }

  // === Vaccination Queries ===

  async getUpcomingVaccinations(days: number = 30): Promise<Vaccination[]> {
    const client = getApiClient();
    const response = await client.get<ApiVaccinationResponse[]>(`v1/pets/vaccinations/upcoming?days=${days}`);
    return response.map(mapApiVaccinationToVaccination);
  }

  async getOverdueVaccinations(): Promise<Vaccination[]> {
    const client = getApiClient();
    const response = await client.get<ApiVaccinationResponse[]>('v1/pets/vaccinations/overdue');
    return response.map(mapApiVaccinationToVaccination);
  }
}
